import React from "react";
import {
  FormControl,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import { InputBaseComponentInterface } from "./types";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import TooltipInput from "@components/InputCst/TooltipInput";
import LanguageSelectorObjectLearning from "../LanguageSelector/ObjectLearning";
import LanguageSelectorNotification from "../LanguageSelector/Notification";
import { useParams } from "react-router-dom";

const InputCst: React.FC<TextFieldProps & InputBaseComponentInterface> = (
  props,
) => {
  const { id: idCorporate } = useParams();
  const idCorporateParam = idCorporate ?? "1"; // Default to "1" if idCorporate is undefined
  const theme = useTheme();
  const { t } = useTranslation(["input"]);

  const {
    id,
    label,
    value,
    placeholder = t("generic-placeholder") + label,
    onChange,
    customWidth,
    error = false,
    disabled,
    sx,
    min,
    max,
    isFilterActive,
    endAdornment,
    type,
  } = props;

  const {
    idEntity,
    errorMessage = "",
    languages,
    notificationLanguages,
    idNotification,
    outputChannel,
    notificationPropertyName,
    col,
    maxValue,
    textTooltip,
    tooltipFile,
    tooltipFileIta,
    tooltipFileEn,
    minWidth = true,
    testIdPrefix,
    ariaLabelOverride,
    ...rest
  } = props;

  const labelTestId = testIdPrefix ? `${testIdPrefix}-label` : undefined;
  const inputTestId = testIdPrefix ? `${testIdPrefix}-input` : undefined;
  const inputElementId = testIdPrefix ? `${testIdPrefix}-input` : id;

  return (
    <FormControl
      variant="standard"
      sx={{
        display: "flex",
        alignItems: "center",
        width: customWidth ? customWidth : "auto",
        minWidth: minWidth ? "200px" : "",
        "& .MuiFormLabel-root": {
          maxWidth: "calc(100% - 40px)",
          "&[data-shrink='true']": {
            maxWidth: "calc(133% - 40px)",
          },
        },
        "& .MuiInputBase-root fieldset": {
          borderColor: isFilterActive ? theme.palette.text.primary : "",
        },
        ...sx,
      }}
    >
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      {tooltipFile && (
        <TooltipInput
          inputDialog
          hrefInput
          hrefIta={tooltipFileIta}
          hrefEn={tooltipFileEn}
          disabled={disabled}
        />
      )}
      <TextField
        {...rest}
        sx={{
          width: "100%",
        }}
        size={"small"}
        error={error}
        helperText={
          <span
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              margin: 0,
            }}
          >
            {error ? errorMessage : ""}
          </span>
        }
        id={inputElementId}
        label={label}
        placeholder={placeholder}
        InputLabelProps={{
          ...(labelTestId
            ? {
                id: labelTestId,
                "data-testid": labelTestId,
                ...(ariaLabelOverride
                  ? { "aria-label": ariaLabelOverride }
                  : {}),
              }
            : {}),
        }}
        InputProps={{
          endAdornment: (
            <>
              {endAdornment}
              {languages &&
                idCorporateParam &&
                idEntity &&
                col &&
                languages.secondary_languages.length > 0 && (
                  <LanguageSelectorObjectLearning
                    languages={languages}
                    col={col}
                    id={idCorporateParam}
                    idObject={idEntity}
                    disabled={disabled}
                  />
                )}
              {notificationLanguages &&
                (notificationLanguages.secondary_languages?.length ?? 0) > 0 &&
                idNotification &&
                notificationPropertyName && (
                  <LanguageSelectorNotification
                    languages={notificationLanguages}
                    id={idCorporateParam}
                    idNotification={idNotification}
                    outputChannel={outputChannel}
                    propertyName={notificationPropertyName}
                    label={label}
                    disabled={disabled}
                  />
                )}
            </>
          ),
        }}
        value={value !== undefined && value !== null ? value : ""}
        onChange={onChange}
        onKeyDown={(e: any) => {
          if (type === "number") {
            if (
              e.key === "e" ||
              e.key === "E" ||
              (min !== undefined && min >= 0 && e.key === "-")
            ) {
              e.preventDefault();
            }
            if (
              maxValue &&
              e.target.value.length > Number(maxValue) - 1 &&
              e.key !== "Backspace"
            ) {
              e.preventDefault();
            }
          }
        }}
        disabled={disabled}
        inputProps={{
          maxLength: maxValue,
          max: max,
          min: min ? min : 0,
          ...(inputTestId ? { "data-testid": inputTestId } : {}),
        }}
      />
    </FormControl>
  );
};

export default InputCst;
